import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { createTRPCProxyClient, createWSClient, wsLink } from "@trpc/client";
import type { AppRouter } from "~/../../server/src/api/root";
import { api } from "~/api";
import { getCurrentCustomerId } from "~/AtomWithDynamoDB";
import type { DifyUserType } from "shared/Schemas";
import type { DifyAPITestJob, DifySubmitPayload } from "~/components/difyAPITest/difyAPITest.types";
import {
  createRunFromPayload,
  DEFAULT_DIFY_APP_NAME,
  normalizeDifyAppName,
} from "~/services/difyAPITest/difyAPITestRunService";
import { runWithWorkerPool } from "~/services/difyAPITest/difyAPITestWorkerPool";
import {
  difyAPITestRunnerReducer,
  initialRunnerState,
} from "~/components/difyAPITest/hooks/difyAPITestRunnerReducer";
import { executeReceptionJob } from "~/services/difyAPITest/difyAPITestJobExecutor";

// ローカル環境向けのWebSocketURL
const wsLocalUrl = process.env.REACT_APP_WEB_SOCKET_LOCAL_URL as string;
// CloudFront環境向けのWebSocketURL
const wsCloudfrontUrl = process.env.REACT_APP_WEB_SOCKET_CLOUDFRONT_URL as string;
// DifyAPIテストのタイムアウト時間
const DIFY_APITEST_TIMEOUT_MS = 60000;
// DifyAPIのユーザー識別子
const DIFY_APITEST_USER: DifyUserType = "test";
// 並列実行の最大数
const DIFY_APITEST_MAX_CONCURRENCY = 5;

/**
 * Difyアプリ名を取得する
 * @return Difyアプリ名
 */
const resolveDifyAppName = async () => {
  try {
    const customerId = await getCurrentCustomerId();
    const userInfo = await api.dify.getUserInfo.query({ customerId });

    return normalizeDifyAppName(userInfo?.name);
  } catch (error) {
    console.info("Dify APIテスト用のアプリ名取得に失敗したため既定値を使用します。", error);
    return DEFAULT_DIFY_APP_NAME;
  }
};

/**
 * Dify APIテスト画面用のWebSocket URLを返す
 * @return 接続先の WebSocket URL
 */
const getWsUrl = () => {
  const defaultLocalUrl = wsLocalUrl || "ws://localhost:3000";
  const defaultCloudfrontUrl = wsCloudfrontUrl || defaultLocalUrl;

  if (typeof window === "undefined") {
    return defaultLocalUrl;
  }

  const currentUrl = window.location.href;

  if (currentUrl.startsWith("http://localhost:3000")) {
    return defaultLocalUrl;
  }

  return defaultCloudfrontUrl;
};

/**
 * DifyAPIテスト画面向けの逐次実行ランナー
 * @return 実行状態と操作関数
 */
export const useDifyReceptionBatchRunner = () => {
  const [{ runs, activeRunId }, dispatch] = useReducer(
    difyAPITestRunnerReducer,
    initialRunnerState
  );
  // Difyアプリ名
  const [difyAppName, setDifyAppName] = useState<string | null>(null);
  // Difyアプリ名取得中のPromiseを共有する
  const difyAppNamePromiseRef = useRef<Promise<string> | null>(null);
  // 生のWebSocketクライアントを保持する
  const wsClientRef = useRef<ReturnType<typeof createWSClient> | null>(null);
  // tRPC経由のWebSocketクライアントを保持する
  const trpcWsClientRef = useRef<ReturnType<typeof createTRPCProxyClient<AppRouter>> | null>(null);
  // キャンセル要求済みのRun IDを保持する
  const cancelledRunIdsRef = useRef<Set<string>>(new Set());
  // Runごとの実行中jobとsessionIdの対応を保持する
  const activeJobSessionMapRef = useRef<Map<string, Map<string, string>>>(new Map());

  useEffect(() => {
    const wsClient = createWSClient({
      url: getWsUrl(),
    });

    wsClientRef.current = wsClient;
    trpcWsClientRef.current = createTRPCProxyClient<AppRouter>({
      links: [
        wsLink({
          client: wsClient,
        }),
      ],
    });

    return () => {
      wsClient.close();
      wsClientRef.current = null;
      trpcWsClientRef.current = null;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const ensureDifyAppName = async () => {
      if (difyAppName) {
        return difyAppName;
      }

      if (!difyAppNamePromiseRef.current) {
        difyAppNamePromiseRef.current = resolveDifyAppName().finally(() => {
          difyAppNamePromiseRef.current = null;
        });
      }

      const resolvedAppName = await difyAppNamePromiseRef.current;

      if (isMounted) {
        setDifyAppName((currentAppName) => currentAppName ?? resolvedAppName);
      }

      return resolvedAppName;
    };

    const loadDifyAppName = async () => {
      await ensureDifyAppName();
    };

    loadDifyAppName();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * 実行中ジョブのセッションを登録する
   * @param runId 実行 ID
   * @param jobId ジョブ ID
   * @param sessionId セッション ID
   * @return 登録処理の完了
   */
  const bindActiveJobSession = useCallback((runId: string, jobId: string, sessionId: string) => {
    const runSessions = activeJobSessionMapRef.current.get(runId) ?? new Map<string, string>();
    runSessions.set(jobId, sessionId);
    activeJobSessionMapRef.current.set(runId, runSessions);
  }, []);

  /**
   * 実行中ジョブのセッション登録を解除する
   * @param runId 実行 ID
   * @param jobId ジョブ ID
   * @return 解除処理の完了
   */
  const releaseActiveJobSession = useCallback((runId: string, jobId: string) => {
    const runSessions = activeJobSessionMapRef.current.get(runId);

    if (!runSessions) {
      return;
    }

    runSessions.delete(jobId);

    if (runSessions.size === 0) {
      activeJobSessionMapRef.current.delete(runId);
    }
  }, []);

  /**
   * 質問一覧を順番に送信する
   * @param payload 送信ペイロード
   * @return 送信処理の完了
   */
  const submitQuestions = useCallback(
    async (payload: DifySubmitPayload) => {
      const client = trpcWsClientRef.current;

      if (!client) {
        throw new Error("WebSocketクライアントの初期化に失敗しました。");
      }

      const appName =
        difyAppName ??
        (await (() => {
          if (!difyAppNamePromiseRef.current) {
            difyAppNamePromiseRef.current = resolveDifyAppName().finally(() => {
              difyAppNamePromiseRef.current = null;
            });
          }

          return difyAppNamePromiseRef.current;
        })());

      if (!difyAppName) {
        setDifyAppName((currentAppName) => currentAppName ?? appName);
      }

      // 一つの実行単位(Run)としてまとめる
      const run = createRunFromPayload(payload, appName);

      cancelledRunIdsRef.current.delete(run.runId);
      dispatch({ type: "RUN_ENQUEUED", run });

      try {
        const jobResults = await runWithWorkerPool({
          items: run.jobs,
          maxConcurrency: DIFY_APITEST_MAX_CONCURRENCY,
          shouldStopScheduling: () => cancelledRunIdsRef.current.has(run.runId),
          execute: async (job: DifyAPITestJob) => {
            return await executeReceptionJob({
              runId: run.runId,
              job,
              user: DIFY_APITEST_USER,
              wsClient: client,
              timeoutMs: DIFY_APITEST_TIMEOUT_MS,
              isRunCancelled: () => cancelledRunIdsRef.current.has(run.runId),
              onStatusChange: (status) => {
                dispatch({
                  type: "JOB_STATUS_CHANGED",
                  runId: run.runId,
                  jobId: job.jobId,
                  status,
                });
              },
              onChunk: (chunk) => {
                dispatch({
                  type: "JOB_CHUNK_RECEIVED",
                  runId: run.runId,
                  jobId: job.jobId,
                  chunk,
                });
              },
              onCompleted: (messageId) => {
                dispatch({
                  type: "JOB_COMPLETED",
                  runId: run.runId,
                  jobId: job.jobId,
                  messageId,
                });
              },
              onError: (errorMessage) => {
                dispatch({
                  type: "JOB_FAILED",
                  runId: run.runId,
                  jobId: job.jobId,
                  errorMessage,
                });
              },
              onCancelled: () => {
                dispatch({
                  type: "RUN_CANCEL_REQUESTED",
                  runId: run.runId,
                });
              },
              onSessionBound: bindActiveJobSession,
              onSessionReleased: releaseActiveJobSession,
            });
          },
        });

        const hasError = jobResults.some((result) => result.status === "error");
        const nextStatus = cancelledRunIdsRef.current.has(run.runId)
          ? "cancelled"
          : hasError
            ? "error"
            : "completed";

        dispatch({
          type: "RUN_FINISHED",
          runId: run.runId,
          status: nextStatus,
        });

        if (hasError && nextStatus !== "cancelled") {
          throw new Error("一部の質問でエラーが発生しました。");
        }
      } finally {
        activeJobSessionMapRef.current.delete(run.runId);
      }
    },
    [bindActiveJobSession, difyAppName, releaseActiveJobSession]
  );

  /**
   * 実行中の処理をキャンセルする
   * @param runId 実行ID
   * @return キャンセル処理の完了
   */
  const cancelRun = useCallback(async (runId: string) => {
    cancelledRunIdsRef.current.add(runId);
    const activeSessionIds = [
      ...new Set(activeJobSessionMapRef.current.get(runId)?.values() ?? []),
    ];

    await Promise.allSettled(
      activeSessionIds.map(async (sessionId) => {
        try {
          await api.dify.stopGeneration.mutate({
            sessionId,
            customerId: "",
          });
        } catch (error) {
          console.error("Dify API停止エラー", error);
        }
      })
    );

    dispatch({
      type: "RUN_CANCEL_REQUESTED",
      runId,
    });
  }, []);

  /**
   * 実行履歴をすべてクリアする
   * @return クリア処理の完了
   */
  const clearRuns = useCallback(() => {
    dispatch({ type: "CLEAR_RUNS" });
  }, []);

  const isSubmitting = runs.some((run) => run.status === "running");

  return {
    difyAppName,
    runs,
    isSubmitting,
    activeRunId,
    submitQuestions,
    cancelRun,
    clearRuns,
  };
};
